/**
 * Feature Flag Service
 * Core service for managing feature flags
 */

import { FeatureFlag, FeatureFlagContext, FeatureFlagSource, FeatureFlagConfig } from './types';
import { DEFAULT_CONFIG, PRIORITY_LEVELS } from './constants';
import { parseUrlFlags } from './urlParser';
import { flagStorage } from './storage';
import { mergeFlags, evaluateFlag } from './merger';
import { sanitizeFlagName, isValidContext } from './validator';
import { logger } from '../logger';

export class FeatureFlagService {
  private config: FeatureFlagConfig;
  private flags: Record<string, FeatureFlag> = {};
  private listeners: Set<() => void> = new Set();

  constructor(config: Partial<FeatureFlagConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.initialize();
  }

  private initialize(): void {
    this.loadFlags();
    
    // Listen for URL changes
    if (typeof window !== 'undefined') {
      window.addEventListener('popstate', () => this.loadFlags());
    }
  }

  private loadFlags(): void {
    const sources: FeatureFlagSource[] = [];

    // URL flags (highest priority)
    const urlFlags = parseUrlFlags(this.config.urlPrefix);
    if (Object.keys(urlFlags).length > 0) {
      sources.push({
        priority: PRIORITY_LEVELS.URL,
        flags: urlFlags,
      });
    }

    // Backend flags (when implemented)
    // TODO: Enable when backend is ready
    // const backendFlags = await this.fetchFromBackend();
    // sources.push({
    //   priority: PRIORITY_LEVELS.BACKEND,
    //   flags: backendFlags,
    // });

    // LocalStorage flags
    const storedFlags = flagStorage.load();
    if (Object.keys(storedFlags).length > 0) {
      sources.push({
        priority: PRIORITY_LEVELS.LOCAL_STORAGE,
        flags: storedFlags,
      });
    }

    // Default flags
    sources.push({
      priority: PRIORITY_LEVELS.DEFAULT,
      flags: this.getDefaultFlags(),
    });

    this.flags = mergeFlags(sources);
    this.notifyListeners();

    if (this.config.enableLogging) {
      logger.info('Feature flags loaded', { flags: this.flags });
    }
  }

  private getDefaultFlags(): Record<string, FeatureFlag> {
    return {
      automation: { name: 'automation', enabled: false },
      advancedAnalytics: { name: 'advancedAnalytics', enabled: false },
      betaFeatures: { name: 'betaFeatures', enabled: false },
      newUI: { name: 'newUI', enabled: false },
    };
  }

  isEnabled(flagName: string, context?: FeatureFlagContext): boolean {
    const sanitized = sanitizeFlagName(flagName);
    const flag = this.flags[sanitized];

    if (!flag) return false;
    if (!context || !isValidContext(context)) return flag.enabled;

    return evaluateFlag(flag, context);
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener());
  }

  getAllFlags(): Record<string, FeatureFlag> {
    return { ...this.flags };
  }

  // For future backend integration
  // private async fetchFromBackend(): Promise<Record<string, FeatureFlag>> {
  //   // Implementation will go here
  //   return {};
  // }
}

export const featureFlagService = new FeatureFlagService();
